import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSpecialityList,
  updateSpecialityList,
  updateListParticipants,
  getCommentList,
  createComment,
  getResponsibilityList,
  getAddSpecialityList,
  getUserListForComment,
  putComments,
  getAuditTrail,
} from "../api/details/index";

const initialState = {
  specialtyList: {},
  comments: [],
  //responsibilityList contains all the responsibilities which are present in Ad Responsibility Modal
  responsibilityList: [],
  addspecialtyList: [],
  //selected will contain responsibilities which are selected in Add Responsibility modal
  selectedResponsibilties: [],
  commentUserList: [],
  auditTrail: [],
  loading: true,
  auditLoading: false,
  respCodeIndex: "",
  error: false,
  selected: [],
  commentLoading: true,
};

const reducers = {
  SET_SPECIALITYLIST(state, specialtyList) {
    specialtyList.payload.specialties.forEach((element) => {
      if (element && !element.incentivized) {
        element.incentivized = "";
      }
      if (element && !element.responsibility_code) {
        element.responsibility_code = "";
      }
    });
    state.specialtyList = specialtyList.payload;
  },
  SET_SPECIALITY_LIST(state, specialtyList) {
    state.specialtyList = specialtyList.payload;
  },
  SET_SELECTED_SPECIALITY(state, list) {
    state.selected = list.payload;
  },
  SET_SELECTED_RESPONSIBILITY(state, list) {
    state.selectedResponsibilties = list.payload;
  },
  SET_COMMENTLIST(state, comments) {
    state.comments = comments;
  },
  SET_RESPONSIBILITY_LIST(state, responsibilityList) {
    state.responsibilityList = responsibilityList.payload;
  },
  SET_ADD_SPECIALITYLIST(state, addspecialtyList) {
    state.addspecialtyList = addspecialtyList.payload;
  },
  SET_USERCOMMENTLIST(state, commentUserList) {
    state.commentUserList = commentUserList;
  },
  SET_RESP_INDEX(state, index) {
    state.respCodeIndex = index;
  },
};

const FETCH_AUDIT_TRAIL = createAsyncThunk(
  "details/FETCH_AUDIT_TRAIL",
  async (listUUID) => {
    const { data, status } = await getAuditTrail(listUUID);
    return { data, status, listUUID };
  }
);

const FETCH_OVERVIEW_TABLE_DATA = createAsyncThunk(
  "details/FETCH_OVERVIEW_TABLE_DATA",
  async (DetailsUUID) => {
    const results = await Promise.all([
      getSpecialityList(DetailsUUID),
      getResponsibilityList(),
      getAddSpecialityList(),
    ]);
    // Extract only data and status to avoid non-serializable Axios headers
    const data = results.map(result => ({
      data: result.data,
      status: result.status
    }));
    return { data };
  }
);
const FETCH_COMMENTS_DATA = createAsyncThunk(
  "details/FETCH_COMMENTS_DATA",
  async (DetailsUUID) => {
    const results = await Promise.all([
      getCommentList(DetailsUUID),
      getUserListForComment(),
    ]);
    // Extract only data and status to avoid non-serializable Axios headers
    const data = results.map(result => ({
      data: result.data,
      status: result.status
    }));
    return { data };
  }
);

const FETCH_SPECIALITYLIST = createAsyncThunk(
  "details/FETCH_SPECIALITYLIST",
  async (DetailsUUID) => {
    const { data, status } = await getSpecialityList(DetailsUUID);
    return { data, status };
  }
);

const FETCH_SPECIALITYLIST_FULFILLED = (state, payload) => {
  const { data: specialityList, status } = payload;
  if (status !== 200) {
  } else {
    // using optional chaining to check specialty list array exist or not
    if (!specialityList.data?.specialties) {
      specialityList.data.specialties = [];
    }
    specialityList.data.specialties.forEach((v) => {
      v.selectedSpecialty = false;
      v.show = true;
    });

    // detailsSlice.caseReducers.SET_SPECIALITYLIST(
    //   state,
    //   specialityList.data || {}
    // );
    state.specialtyList = specialityList.data;
  }
};

const UPDATE_SPECIALITYLIST = createAsyncThunk(
  "details/UPDATE_SPECIALITYLIST",
  async (updatedDetails, { dispatch, rejectWithValue }) => {
    try {
      const { data, status } = await updateSpecialityList(updatedDetails);
      if (status === 200) {
        dispatch(FETCH_SPECIALITYLIST(updatedDetails.UUID));
        dispatch(FETCH_AUDIT_TRAIL(updatedDetails.UUID));
      }
      return { data, status, updatedDetails };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

const UPDATE_LIST_PARTICIPANTS = createAsyncThunk(
  "details/UPDATE_LIST_PARTICIPANTS",
  async (participantsPayload, { dispatch, rejectWithValue }) => {
    try {
      const { data, status } = await updateListParticipants(participantsPayload);
      if (status === 200) {
        dispatch(FETCH_SPECIALITYLIST(participantsPayload.uuid));
      }
      return { data, status };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

const FETCH_COMMENTLIST = createAsyncThunk(
  "details/FETCH_COMMENTLIST",
  async (DetailsUUID) => {
    const { data, status } = await getCommentList(DetailsUUID);
    return { data, status };
  }
);

const FETCH_COMMENTLIST_FULFILLED = (state, payload) => {
  const { data: commentList, status } = payload;
  if (status !== 200) {
  } else {
    detailsSlice.caseReducers.SET_COMMENTLIST(state, commentList.data || []);
  }
};

const CREATE_COMMENT = createAsyncThunk(
  "details/CREATE_COMMENT",
  async (commentObj, { dispatch }) => {
    const { status } = await createComment(commentObj);
    let dataFetching = true;
    if (status === 200) {
      dispatch(FETCH_COMMENTLIST(commentObj.list_uuid));
    } else {
      dataFetching = false;
    }
    return dataFetching;
  }
);

const FETCH_RESPONSIBILITY_LIST = createAsyncThunk(
  "details/FETCH_RESPONSIBILITY_LIST",
  async () => {
    const { data, status } = await getResponsibilityList();
    return { data, status };
  }
);

const FETCH_RESPONSIBILITY_LIST_FULFILLED = (state, payload) => {
  const { data: responsibilities, status } = payload;
  if (status !== 200) {
  } else {
    responsibilities.data.forEach((v) => {
      v.isRespSelected = false;
    });
    state.responsibilityList = responsibilities.data || [];
  }
};

const FETCH_ADD_SPECIALTIES_LIST = createAsyncThunk(
  "details/FETCH_ADD_SPECIALTIES_LIST",
  async (searchKeyword) => {
    const { data, status } = await getAddSpecialityList(searchKeyword);
    return { data, status };
  }
);

const FETCH_ADD_SPECIALTIES_LIST_FULFILLED = (state, payload) => {
  const { data: addSpecialtyList, status } = payload;
  if (status !== 200) {
  } else {
    addSpecialtyList.data.forEach((itm) => {
      itm.isChecked = false;
    });
    state.addspecialtyList = addSpecialtyList.data || [];
  }
};

// UPDATE_RESP_CODE_INDEX

const FETCH_USER_LIST = createAsyncThunk(
  "details/FETCH_USER_LIST",
  async () => {
    const { data, status } = await getUserListForComment();
    return { data, status };
  }
);

const FETCH_USER_LIST_FULFILLED = (state, payload) => {
  const { data: commentList, status } = payload;
  if (status !== 200) {
  } else {
    detailsSlice.caseReducers.SET_USERCOMMENTLIST(
      state,
      commentList.data || []
    );
  }
};

const UPDATE_COMMENTS = createAsyncThunk(
  "details/UPDATE_COMMENTS",
  async (commentObj, { dispatch }) => {
    const { status, data } = await putComments(commentObj);
    return { status, data };
  }
);

const detailsSlice = createSlice({
  name: "details",
  initialState,
  reducers,
  extraReducers(builder) {
    builder.addCase(FETCH_OVERVIEW_TABLE_DATA.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(
      FETCH_OVERVIEW_TABLE_DATA.fulfilled,
      (state, { payload }) => {
        const { data } = payload;
        FETCH_SPECIALITYLIST_FULFILLED(state, data[0]);
        FETCH_RESPONSIBILITY_LIST_FULFILLED(state, data[1]);
        FETCH_ADD_SPECIALTIES_LIST_FULFILLED(state, data[2]);
        state.loading = false;
      }
    );
    builder.addCase(FETCH_OVERVIEW_TABLE_DATA.rejected, (state, action) => {
      state.loading = false;
    });

    builder.addCase(FETCH_COMMENTS_DATA.pending, (state, action) => {
      state.commentLoading = true;
    });
    builder.addCase(FETCH_COMMENTS_DATA.fulfilled, (state, { payload }) => {
      const { data } = payload;
      FETCH_COMMENTLIST_FULFILLED(state, data[0]);
      FETCH_USER_LIST_FULFILLED(state, data[1]);
      state.commentLoading = false;
    });
    builder.addCase(FETCH_COMMENTS_DATA.rejected, (state, action) => {
      state.commentLoading = false;
    });

    builder.addCase(FETCH_SPECIALITYLIST.fulfilled, (state, { payload }) => {
      FETCH_SPECIALITYLIST_FULFILLED(state, payload);

      state.loading = false;
    });
    builder.addCase(FETCH_SPECIALITYLIST.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(FETCH_SPECIALITYLIST.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(UPDATE_SPECIALITYLIST.fulfilled, (state, { payload }) => {
      const { status, updatedDetails } = payload;
      if (status !== 200) {
      } else {
        FETCH_SPECIALITYLIST(updatedDetails.UUID);
      }
      state.loading = false;
    });
    builder.addCase(UPDATE_SPECIALITYLIST.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(UPDATE_SPECIALITYLIST.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(UPDATE_LIST_PARTICIPANTS.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(UPDATE_LIST_PARTICIPANTS.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(UPDATE_LIST_PARTICIPANTS.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(FETCH_COMMENTLIST.fulfilled, (state, { payload }) => {
      FETCH_COMMENTLIST_FULFILLED(state, payload);
      state.commentLoading = false;
    });
    builder.addCase(FETCH_COMMENTLIST.pending, (state, action) => {
      state.commentLoading = true;
    });
    builder.addCase(FETCH_COMMENTLIST.rejected, (state, action) => {
      state.commentLoading = false;
    });
    builder.addCase(CREATE_COMMENT.fulfilled, (state, { payload }) => {
      if (!payload) {
        state.commentLoading = false;
      }
    });
    builder.addCase(CREATE_COMMENT.pending, (state, action) => {
      state.commentLoading = true;
    });
    builder.addCase(CREATE_COMMENT.rejected, (state, action) => {
      state.commentLoading = false;
    });
    builder.addCase(
      FETCH_RESPONSIBILITY_LIST.fulfilled,
      (state, { payload }) => {
        FETCH_RESPONSIBILITY_LIST_FULFILLED(state, payload);
        state.loading = false;
      }
    );
    builder.addCase(FETCH_RESPONSIBILITY_LIST.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(FETCH_RESPONSIBILITY_LIST.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(FETCH_ADD_SPECIALTIES_LIST.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(
      FETCH_ADD_SPECIALTIES_LIST.fulfilled,
      (state, { payload }) => {
        FETCH_ADD_SPECIALTIES_LIST_FULFILLED(state, payload);

        state.loading = false;
      }
    );
    builder.addCase(FETCH_ADD_SPECIALTIES_LIST.rejected, (state, action) => {
      state.loading = false;
    });
    builder.addCase(FETCH_USER_LIST.fulfilled, (state, { payload }) => {
      FETCH_USER_LIST_FULFILLED(state, payload);
      state.commentLoading = false;
    });
    builder.addCase(FETCH_USER_LIST.pending, (state, action) => {
      state.commentLoading = true;
    });
    builder.addCase(FETCH_USER_LIST.rejected, (state, action) => {
      state.commentLoading = false;
    });
    builder.addCase(UPDATE_COMMENTS.fulfilled, (state, { payload }) => {
      const { data: commentList, status } = payload;
      if (status === 200) {
        state.comments = commentList.data;
      }
      state.commentLoading = false;
    });
    builder.addCase(UPDATE_COMMENTS.pending, (state, action) => {
      state.commentLoading = true;
    });
    builder.addCase(UPDATE_COMMENTS.rejected, (state, action) => {
      state.commentLoading = false;
    });
    builder.addCase(FETCH_AUDIT_TRAIL.pending, (state) => {
      state.auditLoading = true;
    });
    builder.addCase(FETCH_AUDIT_TRAIL.fulfilled, (state, { payload }) => {
      const { data, status, listUUID } = payload;
      if (status === 200) {
        const currentUUID = state.specialtyList?.uuid;
        if (!currentUUID || !listUUID || listUUID === currentUUID) {
          state.auditTrail = data.data || [];
        }
      }
      state.auditLoading = false;
    });
    builder.addCase(FETCH_AUDIT_TRAIL.rejected, (state) => {
      state.auditLoading = false;
    });
  },
});
export const {
  SET_RESPONSIBILITY_LIST,
  SET_SELECTED_RESPONSIBILITY,
  SET_ADD_SPECIALITYLIST,
  SET_SELECTED_SPECIALITY,
  SET_SPECIALITYLIST,
  SET_SPECIALITY_LIST,
  SET_RESP_INDEX,
} = detailsSlice.actions;
export {
  FETCH_SPECIALITYLIST,
  UPDATE_SPECIALITYLIST,
  UPDATE_LIST_PARTICIPANTS,
  FETCH_COMMENTLIST,
  CREATE_COMMENT,
  FETCH_RESPONSIBILITY_LIST,
  FETCH_ADD_SPECIALTIES_LIST,
  FETCH_USER_LIST,
  UPDATE_COMMENTS,
  FETCH_OVERVIEW_TABLE_DATA,
  FETCH_COMMENTS_DATA,
  FETCH_AUDIT_TRAIL,
};

export default detailsSlice.reducer;
